import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, output, viewChild, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PetInformationComponent } from '@app/components/booking-steps/pet-information/pet-information.component';
import { PetListComponent } from '@app/components/booking-steps/pet-list/pet-list.component';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { Sheet } from '@app/enums/sheet.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { CreditCard } from '@app/models/creditCard.model';
import { CreditCardProcessingMethods } from '@app/models/creditCardProcessingMethodEnum';
import { Employee } from '@app/models/employee.model';
import { Pet } from '@app/models/pet.model';
import { RescheduleAppointmentRequest } from '@app/models/rescheduleAppointmentRequest.model';
import { Service } from '@app/models/service.model';
import { Site } from '@app/models/site.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { AppointmentRequestItem, DateTimeOfDay } from '@app/models/syncAppointmentRequest.model';
import { StripeApiService } from '@app/services/api/stripe-api.service';
import { AppointmentRequestRefreshService } from '@app/services/appointment-request-refresh.service';
import { AppointmentRequestService } from '@app/services/appointment-request.service';
import { AppointmentService } from '@app/services/appointment.service';
import { ConfigService } from '@app/services/config/config.service';
import { CreditCardService } from '@app/services/credit-card.service';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import { LOCAL_DB_TABLE_NAME, LocalDbService } from '@app/services/local-db.service';
import {
    APPOINTMENT_REQUEST,
    CUSTOMER_ID,
    LocalStorageService,
    OLD_APPOINTMENT_REQUEST,
    PET_LIST,
    SITE,
    SITE_BOOKING_SETTINGS,
} from '@app/services/local-storage.service';
import { LocaleService } from '@app/services/locale.service';
import { PetService } from '@app/services/pet.service';
import { StripeElementsService } from '@app/services/stripe-elements.service';
import { StripeService } from '@app/services/stripe.service';
import { UserAccountService } from '@app/services/user-account.service';
import { dateToStringNoTimeZone, getBookingDate, getBookingTime, hexToRgb, sanitizeColor } from '@app/shared/helpers/extensions';
import { provideIcons } from '@ng-icons/core';
import { lucideShield } from '@ng-icons/lucide';
import { BrnSheet } from '@spartan-ng/brain/sheet';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { toast } from 'ngx-sonner';
import { fromEvent, lastValueFrom, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { IBookingStepComponent } from '../IBookingStepComponent';

@Component({
    selector: 'app-review-request',
    templateUrl: './review-request.component.html',
    styleUrls: ['./review-request.component.css'],
    standalone: false,
    providers: [provideIcons({ lucideShield })],
})
export class ReviewRequestComponent implements OnInit, AfterViewInit, OnDestroy, IBookingStepComponent {
    public creditCardForm!: FormGroup;
    @ViewChild('cCInformationContainer') cCInformationContainer!: ElementRef;
    public url: string = '';
    public isServicePriceAndDurationFeatureActive: boolean = false;
    private ngUnsubscribe: Subject<any> = new Subject();

    appointmentRequest!: AppointmentRequest;
    oldAppointmentRequest!: AppointmentRequest;
    customerID!: string;
    siteSettings!: SiteBookingSettings;
    supportsPets!: boolean;
    shouldShowServicePrice = false;
    showCommentsField = false;
    shouldShowServiceDuration = false;
    requireCCWhenBooking = false;
    duration!: number;
    depositAmount!: number;
    showCCInformation = false;
    creditCardProcessingEnabled = false;
    creditCardProcessingMethod: CreditCardProcessingMethods = CreditCardProcessingMethods.None;
    featureEnabledAndUserLoggedIn = false;
    userAccountsFeatureEnabled = false;
    servicesWithDepositRequired: Service[] = [];
    public userLoggedIn = false;
    comments?: string;
    stripeSurchargeEnabled = false;
    stripeSurchargeRate!: number;
    currencyCode: string = '';
    shouldShowCancellationPolicy = false;
    isTextLoaded = false;
    isRescheduleAppointment = false;
    isEditClicked = false;
    sheet = Sheet;
    isBookAppointmentClicked = false;
    isCreateAccountFlow = false;
    isRequestAppointmentOnly = false;
    dateTimeOfDayList: DateTimeOfDay[] = [];

    public get isCardConnectEnabled() {
        return this.showCCInformation && this.creditCardProcessingMethod === CreditCardProcessingMethods.CardConnect;
    }

    public get isStripeEnabled() {
        return this.showCCInformation && this.creditCardProcessingMethod === CreditCardProcessingMethods.Stripe;
    }

    public get cardToken() {
        return this.creditCardForm.get('cardToken');
    }

    public get securityCode() {
        return this.creditCardForm.get('securityCode');
    }

    public get expiration() {
        return this.creditCardForm.get('expiration');
    }

    public get zipCode() {
        return this.creditCardForm.get('zipCode');
    }

    public get shouldDisplayCancellationSection(): boolean {
        return this.siteSettings.showCancellationPolicy === null ? this.showCCInformation : this.shouldShowCancellationPolicy;
    }

    public mainSheet = viewChild<BrnSheet>(Sheet.Main);
    public loginSheet = viewChild<BrnSheet>(Sheet.Login);
    public createSheet = viewChild<BrnSheet>(Sheet.Create);
    public checkoutSheet = viewChild<BrnSheet>(Sheet.Checkout);
    public petInfoSheet = viewChild<BrnSheet>(Sheet.PetInfo);
    public petListSheet = viewChild<BrnSheet>(Sheet.PetList);
    @ViewChild('petInfoComponent') petInfoComponent?: PetInformationComponent;
    @ViewChild('petListComponent') petListComponent?: PetListComponent;

    public goBack = output<void>();
    public isLoading = true;
    public isPetFormValid = false;
    public isPetSelected = false;
    public isPetSelectionRequired = false;
    private petInfoOpenedFromPetList = false;
    private isAccountCreationInProgress = false;

    private siteBookingSettingsSubscription!: Subscription;

    constructor(
        private router: Router,
        private localStorageService: LocalStorageService,
        private featureFlagService: FeatureFlagService,
        private creditCardService: CreditCardService,
        private fb: FormBuilder,
        private stripeElementsService: StripeElementsService,
        private userAccountService: UserAccountService,
        public dialog: HlmDialogService,
        private stripeApiService: StripeApiService,
        private readonly configService: ConfigService,
        private localeService: LocaleService,
        private localDbCache: LocalDbService,
        private stripeService: StripeService,
        private recaptchaV3Service: ReCaptchaV3Service,
        private appointmentService: AppointmentService,
        private appointmentRequestService: AppointmentRequestService,
        private appointmentRequestRefreshService: AppointmentRequestRefreshService,
        private petService: PetService
    ) {}

    // Implementation of IBookingStepComponent
    public hideSummarySheet = true;
    public previousStepRoute: string = 'datetime';
    public titleBarTitle = 'Review Details';
    public showTitleBarBackButton = true;

    async ngOnInit() {
        this.setIsStepFormValid(false);
        this.getDataFromLocalStorage();
        this.siteBookingSettingsSubscription = this.localStorageService.localStorageSiteBookingSettings$.subscribe(
            (siteBookingSettings) => {
                this.siteSettings = siteBookingSettings ?? (this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings);
            }
        );
        this.customerID = this.localStorageService.getString(CUSTOMER_ID);
        this.userLoggedIn = this.userAccountService.getCurrentUser() !== undefined;
        this.userAccountsFeatureEnabled =
            this.siteSettings?.allowClientOnlineAccountAccess === true && this.featureFlagService.getFeatureFlag(FeatureKey.UserAccounts);
        this.featureEnabledAndUserLoggedIn = this.userAccountsFeatureEnabled && this.userLoggedIn;
        const forceRefreshFirstAvailable = !(
            this.featureFlagService.getFeatureFlag(FeatureKey.DSBRequestOnly) && this.siteSettings.requestAppointmentOnly
        );

        await this.appointmentRequestRefreshService.refreshAppointmentRequestIfNeeded(
            this.appointmentRequest,
            '/booking/service',
            forceRefreshFirstAvailable
        );
        // Reload appointment request after potential refresh
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.getDataFromLocalDB();
        await this.localeService.loadLocaleFile(this.siteSettings.customerCultureCode, true);
        this.currencyCode = this.localeService.getCurrencyCode() || 'USD';
        this.isServicePriceAndDurationFeatureActive = this.featureFlagService.getFeatureFlag(FeatureKey.ShowPriceAndDuration);
        this.showCommentsField = this.featureFlagService.getFeatureFlag(FeatureKey.RequestComments);
        // Show prices if either showPricesWithServices OR showPricesWithEmployees is true
        this.shouldShowServicePrice =
            this.isServicePriceAndDurationFeatureActive &&
            (this.siteSettings.showPricesWithServices || this.siteSettings.showPricesWithEmployees);
        this.shouldShowServiceDuration = this.isServicePriceAndDurationFeatureActive;
        this.requireCCWhenBooking = this.siteSettings.requireCCWhenBooking ?? false;
        this.creditCardProcessingEnabled = this.siteSettings.creditCardProcessingEnabled ?? false;
        this.stripeSurchargeEnabled = this.siteSettings.stripeSurchargeEnabled ?? false;
        this.dateTimeOfDayList = this.appointmentRequest?.dateTimeOfDayList ?? [];
        this.isRequestAppointmentOnly =
            this.featureFlagService.getFeatureFlag(FeatureKey.DSBRequestOnly) && (this.siteSettings.requestAppointmentOnly ?? false);
        this.shouldShowCancellationPolicy =
            this.siteSettings.showCancellationPolicy !== false && !!this.siteSettings.apptCancellationPolicy;
        /* istanbul ignore next */
        this.creditCardProcessingMethod = this.siteSettings.creditCardProcessingMethod ?? CreditCardProcessingMethods.None; // NOSONAR the none will never occur
        this.createCreditCardForm();
        if (this.siteSettings && this.appointmentRequest) {
            this.setIsStepFormValid(true);
        }

        this.showCCInformation = this.creditCardProcessingEnabled && (this.isDepositRequired() || this.requireCCWhenBooking);
        if (!this.showCCInformation) {
            this.setIsLoading(false);
        }
        this.depositAmount = this.calculateTotalDepositAmount();
        this.servicesWithDepositRequired = this.appointmentRequest.service.filter((x) => x.depositRequired);
        if (this.isCardConnectEnabled) {
            this.createCreditCardUrl();
            try {
                // Listen to credit card number iframe event
                fromEvent(window, 'message')
                    .pipe(takeUntil(this.ngUnsubscribe))
                    .subscribe((event: any) => {
                        if ((event as MessageEvent).origin.indexOf('cardconnect.com') >= 0) {
                            const response = event['data'];
                            if (response) {
                                const data = JSON.parse(response);
                                this.setCardTokenFromIframe(data.message);
                            }
                        }
                    });

                this.setupCreditCardFormValidation();
                this.setIsLoading(false);
            } catch {
                this.displayCCInitializingErrorMsg();
            }
        }

        if (this.isStripeEnabled) {
            await this.initializeStripeElements();
        }

        this.userAccountService.userAccountUpdated$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((userAccount) => {
            this.refreshLoginState(!!userAccount);
            this.isTextLoaded = true;
            this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;

            if (!this.userLoggedIn) {
                this.clearClientAndPetInfo();
                this.clearSSOAuthTokensFromCookies();
                this.updatePetSelectionRequirement();
            } else if (this.isAnyAuthSheetOpen()) {
                // Prevent petList$ subscription from racing with sheet (closed) handlers
                this.isAccountCreationInProgress = true;
            }
        });

        if (this.supportsPets) {
            this.petService.petList$
                .pipe(
                    distinctUntilChanged((prev, curr) => {
                        if (prev === null && curr === null) {
                            return true;
                        }
                        if (prev === null || curr === null) {
                            return false;
                        }
                        return prev.length === curr.length;
                    }),
                    filter((petList): petList is Pet[] => petList !== null),
                    takeUntil(this.ngUnsubscribe)
                )
                .subscribe((petList) => this.onPetListUpdated(petList));
        }

        if (this.stripeSurchargeEnabled) {
            this.stripeSurchargeRate = await this.stripeApiService.getStripeSurchargeRate();
        }

        this.isTextLoaded = true;
        this.checkAndShowPetSheets();
        this.updatePetSelectionRequirement();
    }

    /** True if any auth sheet (Main, Login, Create) is open. */
    private isAnyAuthSheetOpen(): boolean {
        return (
            this.mainSheet()?.stateComputed() === 'open' ||
            this.loginSheet()?.stateComputed() === 'open' ||
            this.createSheet()?.stateComputed() === 'open'
        );
    }

    /** True if the Checkout sheet is open. */
    private isCheckoutSheetOpen(): boolean {
        return this.checkoutSheet()?.stateComputed() === 'open';
    }

    /** Updates login-related flags after login or logout. */
    private refreshLoginState(isLoggedIn: boolean): void {
        this.userLoggedIn = isLoggedIn;
        this.featureEnabledAndUserLoggedIn = this.userAccountsFeatureEnabled && this.userLoggedIn;
    }

    protected setIsLoading(isLoading: boolean): void {
        this.isLoading = isLoading;
    }
    protected setIsStepFormValid(isValid: boolean): void {
        // No implementation needed here for this component, it exists to be overridden in the classic version
    }
    protected shouldShowSpinnerOnButton(value: boolean): void {
        // No implementation needed here for this component, it exists to be overridden in the classic version
    }

    ngAfterViewInit() {
        if (!this.featureEnabledAndUserLoggedIn && !this.hasValidClient() && !this.isRescheduleAppointment) {
            if (this.userAccountsFeatureEnabled) {
                this.mainSheet()?.open();
            } else {
                this.onOpenSheet(Sheet.Checkout);
            }
        } else if (!this.userAccountsFeatureEnabled && this.hasValidClient() && this.supportsPets && !this.hasPetInfo()) {
            this.onOpenSheet(Sheet.PetInfo);
        }
    }

    private async initializeStripeElements() {
        try {
            const stripeElements = await this.stripeElementsService.createStripeElementsForSetup(this.currencyCode);
            if (stripeElements) {
                const paymentElement = stripeElements.create('payment', {});
                setTimeout(() => {
                    paymentElement.mount('#stripe-payment-element');
                    this.creditCardService.setStripePaymentElements(stripeElements);
                    this.setIsLoading(false);
                }, 0);
            } else {
                this.displayCCInitializingErrorMsg();
            }
        } catch (error) {
            this.displayCCInitializingErrorMsg();
        }
    }

    private displayCCInitializingErrorMsg() {
        this.showErrorMessage();
        this.setIsStepFormValid(false);
        this.setIsLoading(false);
    }

    protected showErrorMessage(): void {
        //don't auto-close the snackbar, so the user can read the message
        // this.snackBar.open('Error initializing credit card form, please refresh the page', 'Close', {
        //     duration: undefined,
        // });
        toast('', { description: 'Error initializing credit card form, please refresh the page', duration: 0 });
    }

    getServicesPrice(services: Service[]) {
        return services.reduce((total, service) => {
            const price = this.isRequestOnlyAndFirstAvailableEmployee()
                ? (service.lowestPrice ?? service.price ?? 0)
                : (this.appointmentRequest.employee[0].employeeServicePriceList?.find((ep) => ep?.serviceID === service.id)?.price ?? 0);
            return total + (price ?? 0);
        }, 0);
    }

    isDepositRequired() {
        return this.appointmentRequest.service.some((service) => service.depositRequired);
    }

    isDepositExist() {
        return this.isDepositRequired() && this.siteSettings.creditCardProcessingEnabled;
    }

    getServicesDuration(services: Service[]) {
        return services.reduce((total, service) => {
            let duration = 0;
            if (this.isRequestOnlyAndFirstAvailableEmployee()) {
                duration = service.hasDurationException ? (service.longestDuration ?? service.duration ?? 0) : (service.duration ?? 0);
            } else {
                const durationException = this.appointmentRequest.employee[0].durationExceptionList?.find(
                    (de) => de.serviceID === service.id
                );
                duration = durationException ? durationException.duration : service.duration;
            }
            return total + duration;
        }, 0);
    }

    getTotalPrice(): number {
        return this.getServicesPrice(this.appointmentRequest?.service ?? []);
    }

    getTotalDuration(): number {
        return this.getServicesDuration(this.appointmentRequest?.service ?? []);
    }

    private setupCreditCardFormValidation() {
        if (this.showCCInformation && this.isCardConnectEnabled) {
            this.setIsStepFormValid(this.creditCardForm.valid);
            this.creditCardForm.valueChanges.subscribe((_) => {
                this.setIsStepFormValid(this.creditCardForm.valid);
                if (this.creditCardForm.valid) {
                    this.setCreditCardInformation();
                }
            });
        }
    }

    private createCreditCardForm() {
        this.creditCardForm = this.fb.group({
            cardToken: new FormControl('', [Validators.required]),
            securityCode: new FormControl('', [
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(4),
                this.isNumberValidator('invalidCVV'),
            ]),
            expiration: new FormControl('', [
                Validators.required,
                Validators.minLength(4),
                Validators.maxLength(4),
                this.creditCardExpirationValidator(),
            ]),
            zipCode: new FormControl('', [Validators.required, Validators.minLength(5), this.isNumberValidator('invalidZipCode')]),
        });
    }

    private setCardTokenFromIframe(tokenNumber: any) {
        if (tokenNumber) {
            this.creditCardForm.controls['cardToken'].setValue(tokenNumber);
            this.creditCardForm.controls['cardToken'].setErrors(null);
        } else {
            this.creditCardForm.controls['cardToken'].setErrors({ invalidCreditCardNum: true });
            this.setIsStepFormValid(false);
        }

        this.creditCardForm.controls['cardToken'].markAsTouched();
    }

    private createCreditCardUrl() {
        const baseCardConnectUrl = this.configService.config.baseCardConnectUrl;
        const tokenizerPath = '/itoke/ajax-tokenizer.html';

        const urlParams = this.generateCSSAsURLParams();
        const invalidInputEvent = 'true';
        const tokenizeWhenInactive = 'true';
        const inactivityTimeout = '3000';
        const formatInput = 'true';

        this.url =
            `${baseCardConnectUrl}${tokenizerPath}` +
            `${urlParams}&invalidinputevent=${invalidInputEvent}&tokenizewheninactive=${tokenizeWhenInactive}` +
            `&inactivityto=${inactivityTimeout}&formatinput=${formatInput}`;
    }

    private getDataFromLocalStorage() {
        this.siteSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.oldAppointmentRequest = this.localStorageService.get(OLD_APPOINTMENT_REQUEST) as AppointmentRequest;
        const site = this.localStorageService.get(SITE) as Site;
        this.supportsPets = site?.supportsPets;
        this.isRescheduleAppointment = !!this.appointmentRequest?.rescheduleGuid;
    }

    private async getDataFromLocalDB() {
        const employeeIds = this.appointmentRequest?.employee?.map((e) => e.id);
        if (!employeeIds?.length) {
            return;
        }

        const employeeList = (await this.localDbCache.get(LOCAL_DB_TABLE_NAME.employee, 'employeeList')) as Employee[];
        if (!employeeList?.length) {
            return;
        }

        this.appointmentRequest.employee = this.appointmentRequest.employee.map((emp) => {
            const matched = employeeList.find((e) => e.id === emp.id);
            return matched ?? emp;
        });
    }

    private generateCSSAsURLParams() {
        const themeColor = hexToRgb(this.siteSettings.themeColor);

        const sanitizedThemeColor = sanitizeColor(themeColor);

        return (
            `?css=body{margin: 0;}` +
            `input{box-sizing: border-box;outline: none;width: 100%;font-size: 16px;` +
            `height:40px; padding: 8px 10px;border: 1px solid rgb(189, 189, 189);border-radius: 4px;` +
            `color: rgb(34, 34, 34);outline: none;overflow: hidden;}` +
            `input:focus{border: 1px solid ${sanitizedThemeColor};}`
        );
    }

    getBookingDate(datetime: Date) {
        return getBookingDate(datetime);
    }

    getBookingTime(datetime: Date) {
        return getBookingTime(datetime);
    }

    getGroupedDateTimeOfDays(): { date: string; labels: string }[] {
        const grouped = new Map<string, string[]>();
        for (const entry of this.dateTimeOfDayList) {
            const dateKey = entry.date;
            if (!grouped.has(dateKey)) {
                grouped.set(dateKey, []);
            }
            grouped.get(dateKey)!.push(entry.timeOfDay);
        }
        return Array.from(grouped.entries()).map(([date, timeOfDays]) => ({
            date,
            labels: timeOfDays.join(', '),
        }));
    }

    private creditCardExpirationValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const expirationDate = control.value;
            if (!expirationDate) {
                return null;
            }
            const [expirationMonth, expirationYear] = expirationDate.match(/.{2}/g) ?? [0, 0];
            const month = parseInt(expirationMonth, 10);
            const year = parseInt(expirationYear, 10);
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear() % 100;
            const currentMonth = currentDate.getMonth() + 1;
            if (!month || !year || month < 1 || month > 12 || year < currentYear || (year === currentYear && month < currentMonth)) {
                return { creditCardExpired: true };
            }
            return null;
        };
    }

    private isNumberValidator(property: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const value = control.value;
            return !value || isNaN(value) || parseInt(value) < 0 ? { [property]: true } : null;
        };
    }
    public ngOnDestroy() {
        this.creditCardService.resetCreditCardInformation();
        this.creditCardService.resetStripePaymentElements();
        this.ngUnsubscribe.next(null);
        this.ngUnsubscribe.complete();
        this.siteBookingSettingsSubscription.unsubscribe();
    }
    /* istanbul ignore next */
    scrollToCCInformationContainer() {
        this.cCInformationContainer.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }

    private setCreditCardInformation() {
        const [expirationMonth, expirationYear] = this.creditCardForm.get('expiration')!.value.match(/.{2}/g);
        const fullYear = this.getFullYearFromTwoDigitYear(parseInt(expirationYear));
        const creditCard = new CreditCard({
            creditCardToken: this.creditCardForm.get('cardToken')!.value,
            securityCode: this.creditCardForm.get('securityCode')!.value,
            postalCode: this.creditCardForm.get('zipCode')!.value,
            cardHolderName: `${this.appointmentRequest.client.firstName} ${this.appointmentRequest.client.lastName}`,
            expirationYear: fullYear,
            expirationMonth: parseInt(expirationMonth),
            daySmartAccountID: this.customerID,
        });
        this.creditCardService.setCreditCardInformation(creditCard);
    }

    private getFullYearFromTwoDigitYear(year: number) {
        const currentYear = new Date().getFullYear();
        const currentCentury = Math.floor(currentYear / 100) * 100;
        const fullYear = new Date(currentCentury + year, 0).getFullYear();
        return fullYear;
    }

    private calculateTotalDepositAmount() {
        return this.appointmentRequest.service.reduce((sum, service) => {
            let depositAmount = 0;
            if (service.depositRequired) {
                const employeeServicePrice =
                    this.appointmentRequest.employee[0]?.employeeServicePriceList.find((ep) => ep?.serviceID === service.id)?.price ?? 0;
                depositAmount = service.depositPercentage
                    ? (employeeServicePrice * service.depositPercentage) / 100
                    : (service.depositFixedAmount ?? 0);
            }
            return sum + depositAmount;
        }, 0);
    }

    saveComment(): void {
        this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Comments, this.comments);
    }

    public formatPercentage(value: number): string {
        // Check if the number is a whole number
        if (value % 1 === 0) {
            return `${value}%`; // Return without decimals
        }
        // Convert to string and remove trailing zeros
        return `${Number(value.toFixed(2))}%`;
    }

    public onClickEdit() {
        this.isEditClicked = true;
        this.onOpenSheet(Sheet.Checkout);
    }

    private getSheetMap() {
        return {
            [Sheet.Main]: this.mainSheet,
            [Sheet.Login]: this.loginSheet,
            [Sheet.Create]: this.createSheet,
            [Sheet.Checkout]: this.checkoutSheet,
            [Sheet.PetInfo]: this.petInfoSheet,
            [Sheet.PetList]: this.petListSheet,
        };
    }

    public onOpenSheet(sheet: Sheet): void {
        this.getSheetMap()[sheet]?.()?.open();
    }

    /** Closes a sheet. For Login/Create with a logged-in user, starts the auth-to-pet transition. */
    public onCloseSheet(sheet: Sheet): void {
        if (sheet === Sheet.Checkout) {
            this.isEditClicked = false;
        }

        // After login/create, transition to pet sheets
        const isAuthSheetWithLoggedInUser =
            (sheet === Sheet.Login || sheet === Sheet.Create) && this.supportsPets && this.userAccountService.getCurrentUser();

        if (isAuthSheetWithLoggedInUser) {
            this.isCreateAccountFlow = sheet === Sheet.Create;
            this.isAccountCreationInProgress = true;
            this.refreshLoginState(true);
            this.mainSheet()?.close();
            this.getSheetMap()[sheet]?.()?.close();
            this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
            return;
        }

        this.getSheetMap()[sheet]?.()?.close();
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.updatePetSelectionRequirement();
    }

    public onCheckoutFormComplete(sheet: Sheet): void {
        const wasEditClicked = this.isEditClicked;
        this.onCloseSheet(Sheet.Checkout);
        this.onCloseSheet(Sheet.Main);

        if (!this.supportsPets) {
            return;
        }

        // During edit flow, always show pet step so user can update pet selection
        if (wasEditClicked || !this.hasPetInfo()) {
            this.onOpenSheet(Sheet.PetInfo);
        }
    }

    /** Called when account is created directly from the Create Account sheet. */
    public onAccountCreatedFromCreateSheet(): void {
        this.isCreateAccountFlow = true;

        if (this.isAccountCreationInProgress) {
            this.createSheet()?.close();
            return;
        }

        this.isAccountCreationInProgress = true;
        this.refreshLoginState(true);
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.mainSheet()?.close();
        this.createSheet()?.close();
    }

    /** Called when account is created from the nested create-account sheet inside the login sheet. */
    public onAccountCreatedFromLoginSheet(isCreateAccount: boolean = true): void {
        this.isCreateAccountFlow = isCreateAccount;

        if (this.isAccountCreationInProgress) {
            // Auth transition already set up by onCloseSheet — just close the login sheet
            this.loginSheet()?.close();
            return;
        }

        // First time: set up the full auth-to-pet transition
        this.isAccountCreationInProgress = true;
        this.refreshLoginState(true);
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.mainSheet()?.close();
        this.loginSheet()?.close();
    }

    // Auth-to-pet sheet transition: wait for sheet overlays to be removed before opening pet sheets.

    public onMainSheetFullyClosed(): void {
        this.tryOpenPetSheetAfterAuth();
    }

    public onLoginSheetFullyClosed(): void {
        this.tryOpenPetSheetAfterAuth();
    }

    public onCreateSheetFullyClosed(): void {
        this.tryOpenPetSheetAfterAuth();
    }

    /** Waits one tick for overlay cleanup, then opens the pet sheet. */
    private tryOpenPetSheetAfterAuth(): void {
        if (!this.isAccountCreationInProgress) {
            return;
        }

        setTimeout(() => {
            if (!this.isAccountCreationInProgress) {
                return;
            }
            this.openPetSheetAfterAuth();
        }, 0);
    }

    public onGoBack(): void {
        this.isEditClicked = false;
        this.goBack.emit();
    }

    public onClickNotYou() {
        this.userAccountService.logout();
        this.onOpenSheet(Sheet.Main);
    }

    public hasValidClient(): boolean {
        const client = this.appointmentRequest?.client;
        return !!client && Object.keys(client).length > 0;
    }

    public canBookAppointment(): boolean {
        return !this.isPetSelectionRequired;
    }

    // --- Pet helpers ---

    private hasPetInfo(): boolean {
        return (
            this.appointmentRequest?.petInfo !== null &&
            this.appointmentRequest?.petInfo !== undefined &&
            Object.keys(this.appointmentRequest.petInfo).length > 0
        );
    }

    private updatePetSelectionRequirement(): void {
        this.isPetSelectionRequired = this.supportsPets && !this.isRescheduleAppointment && !this.hasPetInfo();
    }

    /** True if PetList or PetInfo sheet is open. */
    private isAnyPetSheetOpen(): boolean {
        return this.petListSheet()?.stateComputed() === 'open' || this.petInfoSheet()?.stateComputed() === 'open';
    }

    /** Fetches the pet list for the current user on init. */
    private checkAndShowPetSheets(): void {
        if (!this.userLoggedIn || !this.supportsPets || this.isRescheduleAppointment) {
            return;
        }

        if (this.hasPetInfo()) {
            return;
        }

        const userAccount = this.userAccountService.getCurrentUser();
        if (!userAccount) {
            return;
        }

        const onlineId = userAccount.onlineId;
        const CID = this.localStorageService.getString(CUSTOMER_ID);

        this.petService.getPetList(CID, onlineId).pipe(takeUntil(this.ngUnsubscribe)).subscribe();
    }

    /** Opens PetList or PetInfo sheet when pet list data arrives, if no other sheet is in the way. */
    private onPetListUpdated(petList: Pet[]): void {
        if (!this.userLoggedIn || this.isRescheduleAppointment) {
            return;
        }
        if (this.hasPetInfo()) {
            return;
        }

        const hasExistingPets = petList.length > 0;

        // Wait one tick for any closing sheet overlays to be removed
        setTimeout(() => {
            if (this.hasPetInfo()) {
                return;
            }
            if (this.isAnyAuthSheetOpen() || this.isCheckoutSheetOpen() || this.isAccountCreationInProgress) {
                return;
            }
            if (this.isAnyPetSheetOpen()) {
                return;
            }

            this.onOpenSheet(hasExistingPets ? Sheet.PetList : Sheet.PetInfo);
        }, 0);
    }

    /** Opens PetList or PetInfo after auth sheets are fully closed. */
    private openPetSheetAfterAuth(): void {
        this.isAccountCreationInProgress = false;

        if (!this.userLoggedIn || !this.supportsPets || this.isRescheduleAppointment || this.hasPetInfo()) {
            return;
        }
        if (this.isAnyPetSheetOpen()) {
            return;
        }

        const petList = (this.localStorageService.get(PET_LIST) as Pet[]) || [];
        if (petList.length > 0) {
            this.petListComponent?.refreshPetList();
            this.onOpenSheet(Sheet.PetList);
        } else {
            this.onOpenSheet(Sheet.PetInfo);
        }
    }

    // --- Pet sheet handlers ---

    public onPetFormValidityChange(isValid: boolean): void {
        this.isPetFormValid = isValid;
    }

    public onPetListValidityChange(isValid: boolean): void {
        this.isPetSelected = isValid;
    }

    public onAddPetComplete(): void {
        if (this.petInfoComponent && this.petInfoComponent.formGroup.valid) {
            const formValues = this.petInfoComponent.formGroup.value;
            const pet: Pet = {
                name: formValues.name,
            };

            if (formValues.petType) {
                pet.petType = formValues.petType;
            }
            if (formValues.petBreed) {
                pet.petBreed = formValues.petBreed;
            }
            if (formValues.weight) {
                pet.weight = formValues.weight;
            }

            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.PetInfo, pet);

            if (this.userLoggedIn) {
                const existingPetList = (this.localStorageService.get(PET_LIST) as Pet[]) || [];
                const petExists = existingPetList.some(
                    (p) =>
                        p.name === pet.name &&
                        p.petType?.name === pet.petType?.name &&
                        p.petBreed?.name === pet.petBreed?.name &&
                        p.weight === pet.weight
                );

                if (this.isCreateAccountFlow) {
                    this.isCreateAccountFlow = false;
                }

                if (!petExists) {
                    existingPetList.push(pet);
                    this.localStorageService.set(PET_LIST, existingPetList);
                }
            }
        }

        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.updatePetSelectionRequirement();
        setTimeout(() => {
            this.onCloseSheet(Sheet.PetInfo);
        }, 0);
    }

    public onAddPetFromList(): void {
        this.petInfoOpenedFromPetList = true;
        this.onCloseSheet(Sheet.PetList);
        setTimeout(() => {
            this.onOpenSheet(Sheet.PetInfo);
            // Reset form for adding a new pet
            this.petInfoComponent?.resetForm();
        }, 0);
    }

    public onBackFromPetInfo(): void {
        if (this.petInfoOpenedFromPetList) {
            this.petInfoOpenedFromPetList = false;
            this.onCloseSheet(Sheet.PetInfo);
            setTimeout(() => {
                if (this.petListComponent) {
                    this.petListComponent.refreshPetList();
                }
                this.onOpenSheet(Sheet.PetList);
            }, 0);
        } else if (this.userLoggedIn && !this.isCreateAccountFlow) {
            this.clearClientAndPetInfo();
            this.updatePetSelectionRequirement();
            this.onCloseSheet(Sheet.PetInfo);
            setTimeout(() => {
                this.onOpenSheet(Sheet.Login);
            }, 0);
        } else if (!this.userLoggedIn) {
            this.onCloseSheet(Sheet.PetInfo);
            setTimeout(() => {
                this.onOpenSheet(Sheet.Checkout);
            }, 0);
        }
    }

    public onPetListComplete(): void {
        if (this.petListComponent && this.petListComponent.formGroup.valid) {
            const selectedPetName = this.petListComponent.formGroup.value.petList;
            const petList = (this.localStorageService.get(PET_LIST) as Pet[]) || [];
            const selectedPet = petList.find((pet) => pet.name === selectedPetName);

            if (selectedPet) {
                this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.PetInfo, selectedPet);
            }
        }

        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.updatePetSelectionRequirement();
        setTimeout(() => {
            this.onCloseSheet(Sheet.PetList);
        }, 0);
    }

    public onBackFromPetList(): void {
        this.clearClientAndPetInfo();
        this.updatePetSelectionRequirement();
        this.onCloseSheet(Sheet.PetList);
        setTimeout(() => {
            this.onOpenSheet(Sheet.Login);
        }, 0);
    }

    public onNotYouFromPetList(): void {
        this.isCreateAccountFlow = false;
        this.userAccountService.logout();
        this.onCloseSheet(Sheet.PetList);
        setTimeout(() => {
            this.onOpenSheet(Sheet.Login);
        }, 0);
    }

    public onClickNotYouFromPetList() {
        this.userAccountService.logout();
        this.onCloseSheet(Sheet.PetList);
        setTimeout(() => {
            this.onOpenSheet(Sheet.Login);
        }, 0);
    }

    public onSwitchPet(): void {
        const petList = (this.localStorageService.get(PET_LIST) as Pet[]) || [];
        this.isCreateAccountFlow = false;

        if (petList.length > 0) {
            setTimeout(() => {
                if (this.petListComponent) {
                    this.petListComponent.refreshPetList();
                }
                this.onOpenSheet(Sheet.PetList);
            }, 0);
        } else {
            this.onOpenSheet(Sheet.PetInfo);
        }
    }

    private clearClientAndPetInfo(): void {
        [LSKey.Client, LSKey.PetInfo].forEach((key) => {
            if (this.appointmentRequest?.[key]) {
                this.localStorageService.removeNested(APPOINTMENT_REQUEST, key);
            }
        });
    }

    public bookAppointment() {
        // Validate pet info before booking on pet-enabled sites
        if (this.supportsPets && !this.hasPetInfo()) {
            const petList = this.localStorageService.get(PET_LIST) as Pet[];
            const hasExistingPets = this.userLoggedIn && petList && Array.isArray(petList) && petList.length > 0;
            this.onOpenSheet(hasExistingPets ? Sheet.PetList : Sheet.PetInfo);
            return;
        }

        this.isBookAppointmentClicked = true;
        this.submitAppointmentRequest();
    }

    private clearSSOAuthTokensFromCookies() {
        const origin = this.configService.config.authService;
        const customerID = this.localStorageService.getString(CUSTOMER_ID);
        const customerIdWithoutPrefix = customerID?.replace(/.*?(\d+)/, '$1'); //NOSONAR
        const url = `${origin}?customerID=${customerIdWithoutPrefix}&removeExistingTokens=1`;
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);
    }
    /**
     * Submits the appointment request after validating the credit card information if required.
     * If the credit card is on file and active, it validates the credit card information before submitting the request.
     * If the credit card is not on file or not active, it directly submits the request.
     */
    private submitAppointmentRequest(): void {
        if (this.isCardConnectEnabled) {
            this.handleCardConnect();
        } else if (this.isStripeEnabled) {
            this.handleStripe();
        } else {
            this.submitRequest();
        }
    }

    private async handleCardConnect(): Promise<void> {
        const creditCard = this.creditCardService.getCreditCardInformation();
        if (!creditCard) {
            this.submitRequest();
            return;
        }

        this.shouldShowSpinnerOnButton(true);
        try {
            const token = await lastValueFrom(this.recaptchaV3Service.execute('validate_credit_card'));
            const isValid = await lastValueFrom(this.creditCardService.validateCreditCard(creditCard, token));
            if (isValid) {
                this.submitRequest();
            } else {
                toast('', { description: 'Credit card is not valid. Please check your data.', duration: 2500 });
                this.isBookAppointmentClicked = false;
            }
        } finally {
            this.shouldShowSpinnerOnButton(false);
        }
    }

    private async handleStripe(): Promise<void> {
        try {
            this.shouldShowSpinnerOnButton(true);
            const stripePaymentElements = this.creditCardService.getStripePaymentElements();
            if (!stripePaymentElements) {
                this.submitRequest();
                return;
            }

            const { error: submitError } = await stripePaymentElements.submit();

            if (submitError) {
                this.showError(submitError.message);
                this.shouldShowSpinnerOnButton(false);
                this.isBookAppointmentClicked = false;
                return;
            }

            const setupIntent = await this.stripeService.createSetupIntent();

            const confirmResult = await this.stripeElementsService.confirmSetupIntent(
                setupIntent.clientSecret,
                stripePaymentElements,
                window.location.href
            );

            if (!confirmResult || confirmResult.error) {
                this.showError(confirmResult?.error.message);
                this.shouldShowSpinnerOnButton(false);
                this.isBookAppointmentClicked = false;
                return;
            }

            const paymentMethodId =
                typeof confirmResult?.setupIntent?.payment_method === 'string'
                    ? confirmResult?.setupIntent?.payment_method
                    : confirmResult?.setupIntent?.payment_method?.id;

            if (!paymentMethodId) {
                this.showError();
                this.shouldShowSpinnerOnButton(false);
                this.isBookAppointmentClicked = false;
                return;
            }

            let currency = this.localeService.getCurrencyCode();
            currency = currency.toLowerCase();
            this.creditCardService.setCreditCardInformation(
                new CreditCard({
                    paymentMethod: paymentMethodId,
                    daySmartAccountID: this.customerID,
                    clientSecret: setupIntent.clientSecret,
                    currency,
                })
            );

            await this.submitRequest();
        } finally {
            this.shouldShowSpinnerOnButton(false);
        }
    }

    private async submitRequest() {
        this.setIsLoading(true);
        // TODO: Test with CC submission
        this.recaptchaV3Service.execute('submit_request').subscribe(async (token: string) => {
            try {
                let response;
                let ticketID: number | undefined;
                if (this.isRescheduleAppointment) {
                    const rescheduleRequest = this.getRescheduleAppointmentRequest();
                    response = await this.appointmentService.rescheduleAppointment(rescheduleRequest).toPromise();
                    ticketID = Number(response?.appointment?.ticketID);
                } else {
                    response = await this.appointmentRequestService.createAppointmentRequest(this.appointmentRequestService, token);
                    ticketID = response?.ticketID;
                }
                if (ticketID || this.isRequestAppointmentOnly) {
                    this.navigateToSubmitted(ticketID);
                } else {
                    this.handleSubmitError('Unable to complete your request');
                }
            } catch (error) {
                const errorMessage = this.extractErrorMessage(error);
                this.handleSubmitError(errorMessage);
            } finally {
                this.setIsLoading(false);
                this.isBookAppointmentClicked = false;
            }
        });
    }

    private extractErrorMessage(error: any): string {
        // Handle Error objects
        if (error instanceof Error) {
            if (error.message.toLowerCase().includes('graphql error')) {
                return error.message.split('GraphQL error:')[1]?.trim() || error.message;
            }
            return error.message;
        }

        // Handle HTTP error responses
        if (error?.error?.message) {
            return error.error.message;
        }
        if (error?.message) {
            return error.message;
        }

        // Fallback message
        return 'An error occurred while submitting your request';
    }

    private handleSubmitError(errorMessage: string): void {
        this.router.navigate(['/booking/error'], {
            queryParams: {
                DSID: this.customerID,
                ErrorMessage: errorMessage,
            },
        });
    }

    private showError(message?: string) {
        toast('', { description: message ?? 'Credit card is not valid. Please check your data.', duration: 2500 });
    }

    private getRescheduleAppointmentRequest(): RescheduleAppointmentRequest {
        const req = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const itemStart = new Date(req.datetime);
        const startDateTime = dateToStringNoTimeZone(itemStart);
        const duration = this.appointmentRequestService.getAppointmentRequestDuration(req);
        const endDateTime = dateToStringNoTimeZone(new Date(itemStart.getTime() + duration * 60000));
        const lastModifiedDateTime = new Date();

        const service = req.service[0];
        const employee = req.employee[0];
        const appointmentRequestItemList: AppointmentRequestItem[] = [];

        if (service && employee) {
            const itemDuration = this.appointmentRequestService.getItemDuration(service, employee);
            const resourceID = service.resourceIDList?.[0];
            const itemEnd = new Date(itemStart.getTime() + itemDuration * 60000);

            appointmentRequestItemList.push(
                new AppointmentRequestItem(
                    service.id,
                    dateToStringNoTimeZone(itemStart),
                    dateToStringNoTimeZone(itemEnd),
                    employee.id,
                    resourceID
                )
            );
        }

        return new RescheduleAppointmentRequest(
            this.customerID,
            startDateTime,
            endDateTime,
            lastModifiedDateTime,
            appointmentRequestItemList,
            req?.comments,
            req?.rescheduleGuid
        );
    }

    private navigateToSubmitted(ticketID?: number) {
        void this.router.navigate(['/booking/submitted'], {
            queryParams: {
                DSID: this.customerID,
            },
            state: {
                ticketID: ticketID,
                shouldNavigate: true,
            },
        });
    }

    public isRequestOnlyAndFirstAvailableEmployee(): boolean {
        return (
            this.isRequestAppointmentOnly &&
            (this.appointmentRequest?.employee?.[0]?.id === '0' || this.appointmentRequest?.employee?.[0]?.firstAvailable === true)
        );
    }

    public anyServiceHasDurationException(): boolean {
        return this.appointmentRequest?.service?.some((service) => service.hasDurationException === true) ?? false;
    }

    public anyServiceHasPriceException(): boolean {
        return this.appointmentRequest?.service?.some((service) => service.hasPriceException === true) ?? false;
    }
}
